import { Component } from '@theme/component';
import { mediaQueryLarge } from '@theme/utilities';

/** The table of contents sits beside the post from this width up. */
const DESKTOP_QUERY = '(min-width: 990px)';

/**
 * @typedef {object} ArticleTocRefs
 * @property {HTMLDetailsElement} details - Collapsible wrapper, kept open beside the post on desktop.
 * @property {HTMLAnchorElement[]} [links] - One link per post heading.
 */

/**
 * Keeps the table of contents open on desktop and marks the heading being read.
 *
 * @extends {Component<ArticleTocRefs>}
 */
class ArticleTocComponent extends Component {
  requiredRefs = ['details'];

  /** @type {AbortController | null} */
  #scrollController = null;

  /** @type {{ heading: HTMLElement, link: HTMLAnchorElement }[]} */
  #sections = [];

  #frame = 0;

  /** @type {MediaQueryList | null} */
  #desktopQuery = null;

  connectedCallback() {
    super.connectedCallback();

    this.#desktopQuery = window.matchMedia(DESKTOP_QUERY);
    this.#desktopQuery.addEventListener('change', this.#syncOpenState);
    this.#syncOpenState();
    this.#observeHeadings();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#syncOpenState();
    this.#observeHeadings();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#desktopQuery?.removeEventListener('change', this.#syncOpenState);
    this.#scrollController?.abort();
    cancelAnimationFrame(this.#frame);
  }

  /**
   * Folds the list away again on small screens once a heading is picked.
   */
  handleLinkClick = () => {
    if (!this.#desktopQuery?.matches) this.refs.details.open = false;
  };

  #syncOpenState = () => {
    if (this.#desktopQuery?.matches) this.refs.details.open = true;
  };

  #observeHeadings() {
    this.#scrollController?.abort();
    this.#sections = [];

    for (const link of this.refs.links ?? []) {
      const heading = document.getElementById(decodeURIComponent(link.hash.slice(1)));
      if (heading) this.#sections.push({ heading, link });
    }

    if (!this.#sections.length) return;

    this.#scrollController = new AbortController();
    window.addEventListener('scroll', this.#scheduleUpdate, { passive: true, signal: this.#scrollController.signal });
    window.addEventListener('resize', this.#scheduleUpdate, { passive: true, signal: this.#scrollController.signal });
    this.#updateCurrent();
  }

  #scheduleUpdate = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(this.#updateCurrent);
  };

  /**
   * Marks the last heading that has scrolled past the top fifth of the viewport.
   */
  #updateCurrent = () => {
    const threshold = window.innerHeight * 0.2;
    let current = null;

    for (const section of this.#sections) {
      if (section.heading.getBoundingClientRect().top > threshold) break;
      current = section;
    }

    for (const { link } of this.#sections) {
      if (link === current?.link) link.setAttribute('aria-current', 'true');
      else link.removeAttribute('aria-current');
    }
  };
}

/**
 * @typedef {object} ArticleShareRefs
 * @property {HTMLButtonElement} copyButton - Copies the post link.
 * @property {HTMLButtonElement} [nativeButton] - Opens the device share sheet where supported.
 * @property {HTMLElement} status - Announces the copy confirmation.
 */

/**
 * Copies the post link and offers the device share sheet on phones.
 *
 * @extends {Component<ArticleShareRefs>}
 */
class ArticleShareComponent extends Component {
  requiredRefs = ['copyButton', 'status'];

  /** @type {number | undefined} */
  #resetTimer;

  connectedCallback() {
    super.connectedCallback();

    const { nativeButton } = this.refs;
    if (nativeButton) nativeButton.hidden = !this.#canShareNatively;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.#resetTimer);
  }

  get #url() {
    return this.dataset.url || window.location.href;
  }

  get #canShareNatively() {
    return typeof navigator.share === 'function' && !mediaQueryLarge.matches;
  }

  copyLink = async () => {
    const { copyButton, status } = this.refs;

    try {
      await navigator.clipboard.writeText(this.#url);
    } catch {
      // Clipboard access can be refused; leave the link visible to copy by hand.
      window.prompt('', this.#url);
      return;
    }

    copyButton.classList.add('is-copied');
    status.textContent = this.dataset.copiedLabel ?? '';

    clearTimeout(this.#resetTimer);
    this.#resetTimer = window.setTimeout(() => {
      copyButton.classList.remove('is-copied');
      status.textContent = '';
    }, 2500);
  };

  shareNative = async () => {
    try {
      await navigator.share({ title: this.dataset.title ?? document.title, url: this.#url });
    } catch {
      // Closing the share sheet rejects the promise; nothing to do.
    }
  };
}

if (!customElements.get('article-toc-component')) {
  customElements.define('article-toc-component', ArticleTocComponent);
}

if (!customElements.get('article-share-component')) {
  customElements.define('article-share-component', ArticleShareComponent);
}
